// 找客中心 v1.12 — Mobile-First 改造
// 设计文档: docs/v1.12-mobile-sourcing-impl.md
// 4 步流程 (Stepper): ① 输入 → ② 结果 → ③ 衍生 → ④ 历史
// v1.12 P0 改造:
//   - 抽 4 个组件: SourcingStepper / SourcingInputBar / DorkCard / DorkActionSheet
//   - 抽 2 个子路由: /sourcing/history / /sourcing/templates
//   - 主路由默认只显示 输入 + 结果, 邮件模板和反馈历史跳子页
//   - mobile first: < 768px 1 个全宽 input + sticky CTA + 紧凑 dork 卡片
//   - 6 个 dork 操作进 action sheet, 6 个按钮不再横排溢出
// 沿用 v1.6-v1.11 的所有 API / query / mutation 调用
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Icon } from '../components/Icon';
import { IntentBadge } from '../components/IntentBadge';
import { Toast } from '../components/Toast';
import { DorkActionSheet } from '../components/sourcing/DorkActionSheet';
import { DorkCard, type DorkRating } from '../components/sourcing/DorkCard';
import { SourcingInputBar, type SourcingIntent } from '../components/sourcing/SourcingInputBar';
import { type SourcingStep, SourcingStepper } from '../components/sourcing/SourcingStepper';
import { Card } from '../components/ui/Card';
import { Spinner } from '../components/ui/Spinner';
import { customerApi, sourcingApi } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';

export const Route = createFileRoute('/sourcing')({
  component: SourcingPage,
});

// 导出给测试用 (jest/vitest 不能直接 import Route.component)
export { SourcingPage };

// v1.37 fix: GRADE_ORDER 提到模块级 (避免每个 render 重建 Record)
const GRADE_ORDER: Record<string, number> = { A: 0, B: 1, C: 2, D: 3 };

// v1.12 P0: Dork 串库筛选 — 静态模板类目 (来自 v1.5)
const CATEGORIES = [
  { value: '', label: '全部' },
  { value: 'find_oem', label: '招 OEM 工厂' },
  { value: 'find_brand', label: '独立品牌' },
  { value: 'find_distributor', label: '分销商' },
  { value: 'find_decision_maker', label: '决策人' },
  { value: 'find_exhibitor', label: '展会参展商' },
  { value: 'find_email', label: '公开邮箱' },
];

const MARKETS = [
  { value: '', label: '全部市场' },
  { value: 'JP', label: '🇯🇵 日本' },
  { value: 'US', label: '🇺🇸 美国' },
  { value: 'GB', label: '🇬🇧 英国' },
  { value: 'GLOBAL', label: '全球' },
];

function SourcingPage() {
  const queryClient = useQueryClient();
  const { currentCompanyId } = useAuthStore();

  // ============ ① 输入 (Layer 2) ============
  const [product, setProduct] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [intent, setIntent] = useState<SourcingIntent>('wholesale');
  const [fillFeedback, setFillFeedback] = useState<Record<string, DorkRating>>({});

  // ============ ② 结果 (DorkActionSheet) ============
  const [activeDork, setActiveDork] = useState<{
    query: string;
    templateId: string;
    reason?: string;
  } | null>(null);

  // ============ ③ 衍生变体 (Layer 3 modal, 沿用 v1.6) ============
  const [deriveOpen, setDeriveOpen] = useState(false);
  const [deriveBase, setDeriveBase] = useState('');
  const [deriveModifiers, setDeriveModifiers] = useState<string[]>([
    'inurl-contact',
    'tbs-qdr-y',
    'exclude-b2c',
  ]);
  const [deriveFeedback, setDeriveFeedback] = useState<Record<string, DorkRating>>({});

  // ============ 实际搜结果 modal (Layer 5, 沿用 v1.7) ============
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchDork, setSearchDork] = useState('');
  const [searchResults, setSearchResults] = useState<
    Array<{ title: string; url: string; snippet: string }>
  >([]);
  const [searchMeta, setSearchMeta] = useState<{
    engine: 'google' | 'bing' | 'duckduckgo';
    fetchStatus: string;
    cached: boolean;
    errorMessage?: string;
  } | null>(null);

  // ============ 静态数据 (Dork 串库 / 目录) — 沿用 v1.5 ============
  const [legacyCategory, setLegacyCategory] = useState('');
  const [legacyMarket, setLegacyMarket] = useState('');
  const [showLegacy, setShowLegacy] = useState(false); // mobile 默认折叠

  // ============ Toast ============
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  // ============ API: queries / mutations (沿用 v1.6-v1.11, 不改) ============
  const { data: templatesData } = useQuery({
    queryKey: ['sourcing-templates'],
    queryFn: () => sourcingApi.listTemplates(),
  });

  const fillMut = useMutation({
    mutationFn: () =>
      sourcingApi.fill({
        product: product.trim(),
        city: city.trim() || undefined,
        country: country.trim() || undefined,
        intent,
      }),
    onSuccess: (data) => {
      setFillFeedback({});
      setToast({
        kind: 'success',
        msg: `生成 ${data.dorks.length} 条 dork${data.fallback ? ' (本地降级)' : ''}`,
      });
    },
    onError: (e) => setToast({ kind: 'error', msg: (e as Error).message }),
  });

  const deriveMut = useMutation({
    mutationFn: () => sourcingApi.derive({ baseQuery: deriveBase, modifiers: deriveModifiers }),
    onSuccess: (data) => {
      setDeriveFeedback({});
      setToast({
        kind: 'success',
        msg: `衍生 ${data.variants.length} 条${data.fallback ? ' (本地降级)' : ''}`,
      });
    },
    onError: (e) => setToast({ kind: 'error', msg: (e as Error).message }),
  });

  const feedbackMut = useMutation({
    mutationFn: (payload: {
      dorkQuery: string;
      dorkTemplateId?: string;
      rating: DorkRating;
    }) => sourcingApi.postFeedback(payload),
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['sourcing-feedback'] });
      setToast({
        kind: 'success',
        msg: vars.rating === 'useful' ? '已记录（赞）' : '已记录（踩）',
      });
    },
    onError: (e) => setToast({ kind: 'error', msg: (e as Error).message }),
  });

  const searchMut = useMutation({
    mutationFn: (dork: string) => sourcingApi.search({ dork, engine: 'google', limit: 10 }),
    onSuccess: (data) => {
      setSearchDork(data.dork);
      setSearchResults(data.results);
      setSearchMeta({
        engine: data.engine,
        fetchStatus: data.fetchStatus,
        cached: data.cached,
        errorMessage: data.errorMessage,
      });
      setSearchOpen(true);
      if (data.fetchStatus === 'ok' && data.results.length > 0) {
        setToast({
          kind: 'success',
          msg: `抓到 ${data.results.length} 条${data.cached ? ' (cache)' : ''}`,
        });
      } else if (data.fetchStatus === 'no_results') {
        setToast({ kind: 'error', msg: 'Google 没结果, 改用其它 dork' });
      } else if (data.fetchStatus === 'captcha' || data.fetchStatus === 'rate_limited') {
        setToast({ kind: 'error', msg: 'Google 拦截, 改用 "Google" 手动跳转' });
      } else if (data.fetchStatus === 'parse_error') {
        setToast({ kind: 'error', msg: '解析失败, 改用 "Google" 手动跳转' });
      } else {
        setToast({ kind: 'error', msg: `抓取失败: ${data.errorMessage ?? data.fetchStatus}` });
      }
    },
    onError: (e) => setToast({ kind: 'error', msg: (e as Error).message }),
  });

  const { data: dorksData } = useQuery({
    queryKey: ['dorks', legacyMarket],
    queryFn: () => sourcingApi.listDorks(legacyMarket || undefined),
  });

  const { data: dirsData } = useQuery({
    queryKey: ['directories', legacyMarket],
    queryFn: () => sourcingApi.listDirectories(legacyMarket || undefined),
  });

  const { data: customersData } = useQuery({
    queryKey: ['customers', currentCompanyId],
    queryFn: () => customerApi.list({ limit: 200 }),
  });

  const filteredDorks = (dorksData?.dorks ?? []).filter(
    (d) => !legacyCategory || d.category === legacyCategory,
  );

  // ============ Handlers ============
  function openGoogle(query: string) {
    window.open(`https://www.google.com/search?q=${encodeURIComponent(query)}`, '_blank');
  }

  function rateDork(query: string, templateId: string, rating: DorkRating) {
    setFillFeedback((s) => ({ ...s, [query]: rating }));
    feedbackMut.mutate({ dorkQuery: query, dorkTemplateId: templateId, rating });
  }

  function rateDerive(query: string, rating: DorkRating) {
    setDeriveFeedback((s) => ({ ...s, [query]: rating }));
    feedbackMut.mutate({ dorkQuery: query, rating });
  }

  function startDerive(baseQuery: string) {
    setDeriveBase(baseQuery);
    setDeriveOpen(true);
  }

  function startActualSearch(dork: string) {
    searchMut.mutate(dork);
  }

  function toggleModifier(id: string) {
    setDeriveModifiers((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));
  }

  function handleInputChange(key: 'product' | 'city' | 'country' | 'intent', value: string) {
    if (key === 'product') setProduct(value);
    else if (key === 'city') setCity(value);
    else if (key === 'country') setCountry(value);
    else if (key === 'intent') setIntent(value as SourcingIntent);
  }

  function handleSubmitFill() {
    fillMut.mutate();
  }

  // 4-dot stepper: 推导当前 active step
  // 1=输入, 2=结果 (fillMut.data 存在), 3=衍生 (deriveMut.data 存在), 4=历史
  // 这里只渲染前 2 步, 因为 3/4 跳子页/弹窗
  const stepperCurrent: SourcingStep = fillMut.data ? 2 : 1;
  const stepperCompleted: SourcingStep[] = fillMut.data ? [1] : [];

  // ============ Render ============
  return (
    <div className="space-y-4 sm:space-y-5 pb-24 sm:pb-6">
      {/* 顶部 PageHeader + Stepper */}
      <div className="space-y-3 animate-fade-up">
        <header>
          <h1 className="text-xl sm:text-2xl font-semibold text-ink leading-tight flex items-center gap-2">
            <Icon name="search" size="md" className="text-accent" aria-hidden />
            找客中心
            <span className="text-[10px] sm:text-xs font-normal text-ink-3 ml-1">v1.12</span>
          </h1>
          <p className="text-xs sm:text-sm text-ink-3 mt-1 leading-relaxed">
            Hybrid 5 层: 模板库 + AI 填充 + 衍生变体 + 反馈学习。零爬虫, 零付费。
          </p>
        </header>
        <SourcingStepper
          current={stepperCurrent}
          completed={stepperCompleted}
          labels={{ 1: '输入', 2: '结果', 3: '衍生', 4: '历史' }}
          onStepClick={(s) => {
            if (s === 3) setDeriveOpen(true);
            if (s === 4) {
              window.location.href = '/sourcing/history';
            }
          }}
        />
      </div>

      {/* ① 输入区 (mobile first) */}
      <SourcingInputBar
        product={product}
        city={city}
        country={country}
        intent={intent}
        isPending={fillMut.isPending}
        onChange={handleInputChange}
        onSubmit={handleSubmitFill}
        hint={
          fillMut.data
            ? `已生成 ${fillMut.data.dorks.length} 条 dork${fillMut.data.fallback ? ' (本地降级)' : ''}`
            : undefined
        }
      />

      {/* v1.34: Intent Signal 概览 — 已有客户按 grade 排序 + 1 键拓客入口 */}
      <IntentSignalOverview customersData={customersData} />

      {/* ② 结果区 (DorkCard 列表) */}
      {fillMut.data && (
        <section aria-label="生成结果" className="animate-fade-up">
          <div className="flex items-center justify-between mb-2.5 px-1">
            <h2 className="text-sm sm:text-base font-semibold text-ink flex items-center gap-1.5">
              <span className="w-1 h-4 bg-accent rounded-sm" aria-hidden />
              结果 ({fillMut.data.dorks.length})
              {fillMut.data.fallback && (
                <span className="text-[10px] sm:text-xs text-warn font-normal flex items-center gap-0.5 ml-1">
                  <Icon name="alert-triangle" size="xs" aria-hidden />
                  本地降级
                </span>
              )}
            </h2>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(JSON.stringify(fillMut.data, null, 2));
                setToast({ kind: 'success', msg: '已复制 JSON' });
              }}
              className="text-xs text-accent hover:underline flex items-center gap-1 min-h-[36px] px-2"
            >
              <Icon name="clipboard-list" size="xs" aria-hidden />
              复制 JSON
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {fillMut.data.dorks.map((d, i) => (
              <DorkCard
                key={`${i}-${d.query}`}
                index={i + 1}
                dork={{
                  query: d.query,
                  templateId: d.templateId,
                  reason: d.reason,
                }}
                rating={fillFeedback[d.query] ?? null}
                onTap={() =>
                  setActiveDork({
                    query: d.query,
                    templateId: d.templateId,
                    reason: d.reason,
                  })
                }
                stagger={Math.min(i, 7)}
              />
            ))}
          </div>

          {/* 行业目录 (在结果下方) */}
          {fillMut.data.directories.length > 0 && (
            <div className="mt-4">
              <h3 className="text-xs font-semibold text-ink-2 mb-2 flex items-center gap-1.5 px-1">
                <Icon name="globe" size="sm" className="text-accent" aria-hidden />
                行业目录 ({fillMut.data.directories.length})
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {fillMut.data.directories.map((dir) => (
                  <a
                    key={dir.url}
                    href={dir.url}
                    target="_blank"
                    rel="noreferrer"
                    className="block bg-surface border border-line rounded-md p-3 hover:border-accent/60 transition-colors"
                  >
                    <div className="text-sm font-medium text-ink-2 hover:text-accent break-all leading-snug">
                      {dir.name}
                    </div>
                    <div className="text-[10px] sm:text-xs text-ink-3 mt-1 leading-relaxed">
                      {dir.note}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* 跳子页: 反馈历史 + 邮件模板 + Intent 拓客 (mobile 主要入口) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 pt-1">
        <SubPageLink to="/sourcing/history" icon="thumbs-up" label="反馈历史" hint="AI 学你偏好" />
        <SubPageLink to="/sourcing/templates" icon="mail" label="邮件模板" hint="24 条 / 3 语言" />
        <SubPageLink
          to="/sourcing/intent"
          icon="zap"
          label="Intent 拓客"
          hint="Dork → A/B/C → 入库"
        />
      </div>

      {/* 静态数据 (Dork 串库 + 行业目录) — mobile 默认折叠, 桌面默认展开 */}
      <Card padding="none" className="overflow-hidden">
        <button
          type="button"
          onClick={() => setShowLegacy((s) => !s)}
          className="w-full px-4 py-3 flex items-center justify-between gap-2 text-left min-h-[48px] hover:bg-surface-2 transition-colors"
          aria-expanded={showLegacy}
          aria-controls="legacy-section"
        >
          <span className="text-sm font-semibold text-ink flex items-center gap-1.5">
            <Icon name="book-open" size="sm" className="text-accent" aria-hidden />
            静态 Dork 串库 + 行业目录
            <span className="text-[10px] sm:text-xs text-ink-3 font-normal">
              ({filteredDorks.length + (dirsData?.directories.length ?? 0)})
            </span>
          </span>
          <Icon
            name={showLegacy ? 'chevron-up' : 'chevron-down'}
            size="sm"
            className="text-ink-3"
            aria-hidden
          />
        </button>
        {showLegacy && (
          <div
            id="legacy-section"
            className="px-4 pb-4 pt-1 border-t border-line space-y-3 animate-fade-up"
          >
            <div className="flex items-center gap-2 flex-wrap pt-2">
              <select
                value={legacyCategory}
                onChange={(e) => setLegacyCategory(e.target.value)}
                className="px-2.5 py-1.5 border border-line rounded text-xs bg-surface"
                aria-label="类目筛选"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
              <select
                value={legacyMarket}
                onChange={(e) => setLegacyMarket(e.target.value)}
                className="px-2.5 py-1.5 border border-line rounded text-xs bg-surface"
                aria-label="市场筛选"
              >
                {MARKETS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
              <Link
                to="/customers"
                className="ml-auto px-3 py-1.5 bg-accent text-white rounded text-xs font-medium hover:bg-accent-2 inline-flex items-center gap-1 min-h-[32px]"
              >
                <Icon name="users" size="xs" aria-hidden />
                潜在客户 ({customersData?.total ?? 0})
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Dork 串库 */}
              <div>
                <h3 className="text-xs font-semibold text-ink-2 mb-2 flex items-center gap-1.5">
                  <Icon name="settings-2" size="sm" className="text-accent" aria-hidden />
                  静态 Dork ({filteredDorks.length})
                </h3>
                <div className="space-y-2">
                  {filteredDorks.slice(0, 6).map((d) => (
                    <div key={d.id} className="bg-surface-2 border border-line rounded p-2.5">
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-medium text-ink">{d.title.zh}</div>
                          <div className="text-[10px] text-ink-3 mt-0.5">
                            {d.market !== 'GLOBAL' ? `${d.market} · ` : ''}
                            {d.description.zh}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => openGoogle(d.query)}
                          className="shrink-0 px-2 py-1 bg-accent text-white rounded text-[10px] hover:bg-accent-2 inline-flex items-center gap-0.5"
                        >
                          <Icon name="link" size="xs" aria-hidden />
                          Google
                        </button>
                      </div>
                      <pre className="text-[10px] text-ink-2 bg-surface p-1.5 rounded overflow-x-auto whitespace-pre-wrap break-all">
                        {d.query}
                      </pre>
                    </div>
                  ))}
                  {filteredDorks.length > 6 && (
                    <p className="text-[10px] text-ink-3 text-center">
                      还有 {filteredDorks.length - 6} 条 · 用类目筛选细看
                    </p>
                  )}
                </div>
              </div>

              {/* 行业目录 */}
              <div>
                <h3 className="text-xs font-semibold text-ink-2 mb-2 flex items-center gap-1.5">
                  <Icon name="globe" size="sm" className="text-accent" aria-hidden />
                  行业目录 ({dirsData?.directories.length ?? 0})
                </h3>
                <div className="space-y-2">
                  {dirsData?.directories.slice(0, 6).map((d) => (
                    <a
                      key={d.id}
                      href={d.url}
                      target="_blank"
                      rel="noreferrer"
                      className="block bg-surface-2 border border-line rounded p-2.5 hover:border-accent/60 transition-colors"
                    >
                      <div className="flex items-center gap-1.5 text-xs font-medium text-ink-2">
                        {d.market === 'JP' ? (
                          <span>🇯🇵</span>
                        ) : d.market === 'US' ? (
                          <span>🇺🇸</span>
                        ) : (
                          <Icon name="globe" size="xs" className="text-ink-3" aria-hidden />
                        )}
                        {d.name}
                      </div>
                      <div className="text-[10px] text-ink-3 mt-0.5 line-clamp-2">
                        {d.description.zh}
                      </div>
                    </a>
                  ))}
                  {dirsData && dirsData.directories.length > 6 && (
                    <p className="text-[10px] text-ink-3 text-center">
                      还有 {dirsData.directories.length - 6} 条
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* SOP 提示 — 保留但 mobile 缩小 */}
      <Card padding="md" className="bg-accent-soft/40 border-accent/20">
        <h2 className="text-sm font-semibold text-ink-2 mb-2 flex items-center gap-1.5">
          <Icon name="calendar-days" size="sm" className="text-accent" aria-hidden />
          每周找客 SOP (10h)
        </h2>
        <div className="grid grid-cols-5 gap-1.5 sm:gap-3 text-[10px] sm:text-xs">
          {[
            { day: '周一', task: '跟 lead', hours: '2h' },
            { day: '周二', task: 'JP', hours: '2h' },
            { day: '周三', task: 'US', hours: '2h' },
            { day: '周四', task: '海关', hours: '2h' },
            { day: '周五', task: '整理', hours: '1h' },
          ].map((d) => (
            <div key={d.day} className="bg-surface rounded p-1.5 sm:p-2.5 text-center">
              <div className="font-semibold text-ink-2 text-[10px] sm:text-xs">
                {d.day} · {d.hours}
              </div>
              <div className="text-ink-3 truncate mt-0.5">{d.task}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* 重要原则提示 */}
      <Card padding="sm" className="bg-warn/8 border-warn/30">
        <p className="text-xs text-ink-2 flex items-start gap-1.5 leading-relaxed">
          <Icon name="pin" size="xs" className="text-warn mt-0.5 shrink-0" aria-hidden />
          <span>
            点链接后 <b>手动浏览</b>, 找到客户点 录入 按钮入库。系统不爬数据。
          </span>
        </p>
      </Card>

      {/* ============ Modals ============ */}

      {/* DorkActionSheet — 6 个操作进 sheet */}
      <DorkActionSheet
        open={!!activeDork}
        dork={activeDork}
        rating={activeDork ? (fillFeedback[activeDork.query] ?? null) : null}
        searchPending={searchMut.isPending}
        onClose={() => setActiveDork(null)}
        onOpenGoogle={() => {
          if (activeDork) openGoogle(activeDork.query);
          setActiveDork(null);
        }}
        onActualSearch={() => {
          if (activeDork) startActualSearch(activeDork.query);
          // 不关 sheet — 让 search modal 接管
          setActiveDork(null);
        }}
        onCopy={() => {
          if (activeDork) {
            navigator.clipboard.writeText(activeDork.query);
            setToast({ kind: 'success', msg: '已复制 query' });
          }
          setActiveDork(null);
        }}
        onDerive={() => {
          if (activeDork) startDerive(activeDork.query);
          setActiveDork(null);
        }}
        onRate={(r) => {
          if (activeDork) rateDork(activeDork.query, activeDork.templateId, r);
        }}
      />

      {/* 衍生变体 modal (沿用 v1.6) */}
      {deriveOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fade-up"
          onClick={() => setDeriveOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setDeriveOpen(false);
          }}
          role="presentation"
        >
          <section
            className="w-full sm:max-w-3xl bg-surface rounded-t-2xl sm:rounded-lg shadow-xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            aria-label="衍生变体对话框"
          >
            <div className="sticky top-0 bg-surface/95 backdrop-blur border-b border-line px-4 py-3 sm:px-5 sm:py-4 flex items-center justify-between">
              <h2 className="text-sm sm:text-base font-semibold">③ 衍生变体 (Layer 3)</h2>
              <button
                type="button"
                onClick={() => setDeriveOpen(false)}
                className="w-9 h-9 sm:w-8 sm:h-8 flex items-center justify-center text-ink-3 hover:text-ink-2 hover:bg-surface-2 rounded-md"
                aria-label="关闭"
              >
                <Icon name="x" size="md" aria-hidden />
              </button>
            </div>
            <div className="p-4 sm:p-5 space-y-3">
              <p className="text-xs text-ink-3">
                base:{' '}
                <code className="bg-surface-2 px-1.5 py-0.5 rounded font-mono text-accent break-all">
                  {deriveBase}
                </code>
              </p>
              <div>
                <h3 className="text-xs font-semibold text-ink-2 mb-2">选 modifier</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {templatesData?.modifiers.map((m) => (
                    <label
                      key={m.id}
                      className={`flex items-start gap-2 p-2.5 rounded text-xs cursor-pointer border transition-colors ${
                        deriveModifiers.includes(m.id)
                          ? 'border-accent bg-accent/10'
                          : 'border-line bg-surface-2 hover:border-ink-3'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={deriveModifiers.includes(m.id)}
                        onChange={() => toggleModifier(m.id)}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-accent text-[11px]">
                          [{m.id}] {m.modifier}
                        </div>
                        <div className="text-ink-3 mt-0.5 leading-relaxed">{m.reason}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => deriveMut.mutate()}
                  disabled={deriveMut.isPending || deriveModifiers.length === 0}
                  className="px-4 py-2 bg-accent text-white rounded text-sm font-medium hover:bg-accent-2 disabled:opacity-50 inline-flex items-center gap-1.5 min-h-[44px]"
                >
                  {deriveMut.isPending ? (
                    <>
                      <Spinner size="xs" variant="ring" />
                      衍生中…
                    </>
                  ) : (
                    <>
                      <Icon name="shuffle" size="sm" aria-hidden />
                      衍生 ({deriveModifiers.length})
                    </>
                  )}
                </button>
              </div>
              {deriveMut.data && (
                <div className="border-t border-line pt-3 space-y-2">
                  {deriveMut.data.fallback && (
                    <p className="text-xs text-warn flex items-center gap-1">
                      <Icon name="alert-triangle" size="xs" aria-hidden />
                      本地降级结果
                    </p>
                  )}
                  {deriveMut.data.variants.map((v, i) => {
                    const rating = deriveFeedback[v.query];
                    return (
                      <div key={`${i}-${v.query}`} className="bg-surface-2 p-2.5 rounded text-sm">
                        <a
                          href={`https://www.google.com/search?q=${encodeURIComponent(v.query)}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={() => openGoogle(v.query)}
                          className="text-ink-2 hover:text-accent break-all font-medium leading-snug"
                        >
                          {v.query}
                        </a>
                        <div className="text-xs text-ink-3 mt-1 flex flex-wrap gap-x-2">
                          {v.modifiers.length > 0 && (
                            <span>modifiers: {v.modifiers.map((mid) => `[${mid}]`).join(' ')}</span>
                          )}
                          <span>{v.reason}</span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-2">
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(v.query);
                              setToast({ kind: 'success', msg: '已复制' });
                            }}
                            className="px-2 py-1 border border-line rounded text-xs hover:bg-surface inline-flex items-center gap-1 min-h-[32px]"
                            title="复制"
                          >
                            <Icon name="copy" size="xs" aria-hidden />
                            复制
                          </button>
                          <button
                            type="button"
                            onClick={() => rateDerive(v.query, 'useful')}
                            disabled={rating === 'useful'}
                            className={`px-2 py-1 rounded text-xs min-h-[32px] ${
                              rating === 'useful'
                                ? 'bg-good/15 text-good border border-good/40'
                                : 'border border-line hover:bg-good/10'
                            }`}
                            aria-label="赞"
                          >
                            <Icon name="thumbs-up" size="xs" aria-hidden />
                          </button>
                          <button
                            type="button"
                            onClick={() => rateDerive(v.query, 'useless')}
                            disabled={rating === 'useless'}
                            className={`px-2 py-1 rounded text-xs min-h-[32px] ${
                              rating === 'useless'
                                ? 'bg-bad/15 text-bad border border-bad/40'
                                : 'border border-line hover:bg-bad/10'
                            }`}
                            aria-label="踩"
                          >
                            <Icon name="thumbs-down" size="xs" aria-hidden />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      {/* 实际搜结果 modal (沿用 v1.7) */}
      {searchOpen && searchMeta && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fade-up"
          onClick={() => setSearchOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setSearchOpen(false);
          }}
          role="presentation"
        >
          <section
            className="w-full sm:max-w-4xl bg-surface rounded-t-2xl sm:rounded-lg shadow-xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            aria-label="实际搜结果"
          >
            <div className="sticky top-0 bg-surface/95 backdrop-blur border-b border-line px-4 py-3 sm:px-5 sm:py-4 flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <h2 className="text-sm sm:text-base font-semibold">⑤ 实际搜结果 (Layer 5)</h2>
                <p className="text-xs text-ink-3 mt-1 break-all">
                  dork:{' '}
                  <code className="bg-surface-2 px-1.5 py-0.5 rounded font-mono text-accent">
                    {searchDork}
                  </code>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSearchOpen(false)}
                className="shrink-0 w-9 h-9 sm:w-8 sm:h-8 flex items-center justify-center text-ink-3 hover:text-ink-2 hover:bg-surface-2 rounded-md"
                aria-label="关闭"
              >
                <Icon name="x" size="md" aria-hidden />
              </button>
            </div>
            <div className="p-4 sm:p-5 space-y-3">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="px-2 py-0.5 bg-accent/10 text-accent rounded font-mono">
                  {searchMeta.engine}
                </span>
                <span
                  className={`px-2 py-0.5 rounded ${
                    searchMeta.fetchStatus === 'ok'
                      ? 'bg-good/15 text-good'
                      : searchMeta.fetchStatus === 'no_results'
                        ? 'bg-warn/15 text-warn'
                        : 'bg-bad/15 text-bad'
                  }`}
                >
                  {searchMeta.fetchStatus}
                </span>
                {searchMeta.cached && (
                  <span className="px-2 py-0.5 bg-surface-2 text-ink-3 rounded">5min cache</span>
                )}
                {searchMeta.errorMessage && (
                  <span className="text-bad text-xs">· {searchMeta.errorMessage}</span>
                )}
              </div>

              {searchResults.length === 0 ? (
                <div className="bg-warn/8 border border-warn/30 rounded p-4 text-sm">
                  <p className="font-medium text-ink mb-1">没拿到结果</p>
                  <p className="text-xs text-ink-2 mb-3">可能 Google 拒绝 / 改版 / 没结果。建议:</p>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => {
                        window.open(
                          `https://www.google.com/search?q=${encodeURIComponent(searchDork)}`,
                          '_blank',
                        );
                      }}
                      className="px-3 py-2 bg-accent text-white rounded text-xs hover:bg-accent-2 inline-flex items-center gap-1 min-h-[36px]"
                    >
                      <Icon name="link" size="xs" aria-hidden />
                      改用手动 Google
                    </button>
                    <button
                      type="button"
                      onClick={() => startActualSearch(searchDork)}
                      className="px-3 py-2 border border-line rounded text-xs hover:bg-surface-2 inline-flex items-center gap-1 min-h-[36px]"
                    >
                      <Icon name="refresh-cw" size="xs" aria-hidden />
                      重试
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {searchResults.map((r, i) => (
                    <div key={`${i}-${r.url}`} className="bg-surface-2 p-3 rounded">
                      <div className="flex items-start gap-2">
                        <span className="text-accent font-mono shrink-0 text-xs">{i + 1}.</span>
                        <div className="flex-1 min-w-0">
                          <a
                            href={r.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm font-medium text-info hover:underline break-all leading-snug"
                          >
                            {r.title}
                          </a>
                          <div className="text-[10px] sm:text-xs text-good mt-0.5 break-all">
                            {r.url}
                          </div>
                          {r.snippet && (
                            <p className="text-xs text-ink-2 mt-1.5 leading-relaxed">{r.snippet}</p>
                          )}
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(r.url);
                              setToast({ kind: 'success', msg: '已复制 URL' });
                            }}
                            className="px-2 py-1 border border-line rounded text-xs hover:bg-surface inline-flex items-center gap-0.5 min-h-[32px]"
                            title="复制 URL"
                          >
                            <Icon name="clipboard-list" size="xs" aria-hidden />
                            URL
                          </button>
                          <button
                            type="button"
                            onClick={() => rateDork(searchDork, 'search-result', 'useful')}
                            className="px-2 py-1 border border-good/40 text-good rounded text-xs hover:bg-good/10 min-h-[32px]"
                            title="赞"
                            aria-label="赞"
                          >
                            <Icon name="thumbs-up" size="xs" aria-hidden />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-ink-3 text-center pt-1">
                    找到客户? 自己决定是否录入 — 系统不自动入库
                  </p>
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      {toast && <Toast kind={toast.kind} message={toast.msg} onClose={() => setToast(null)} />}
    </div>
  );
}

// ============ 内部: 子页 Link 卡片 ============

function SubPageLink({
  to,
  icon,
  label,
  hint,
}: {
  to: '/sourcing/history' | '/sourcing/templates' | '/sourcing/intent';
  icon: string;
  label: string;
  hint: string;
}) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-2.5 p-3 sm:p-3.5 bg-surface border border-line rounded-md hover:border-accent/60 hover:shadow-sm transition-all min-h-[56px] focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2"
    >
      <span className="shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-md bg-accent/10 text-accent flex items-center justify-center group-hover:bg-accent group-hover:text-white transition-colors">
        <Icon name={icon} size="md" aria-hidden />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-ink leading-tight">{label}</div>
        <div className="text-[10px] sm:text-xs text-ink-3 mt-0.5 leading-tight">{hint}</div>
      </div>
      <Icon
        name="chevron-right"
        size="md"
        className="text-ink-3 group-hover:text-accent shrink-0"
        aria-hidden
      />
    </Link>
  );
}

// ============ 内部: v1.34 Intent Signal 概览卡片 ============
// 显示最近 5 个客户, 按 grade (A→D) 排序, badge 在桌面列首 / 移动公司名右侧
// 数据来源: customer.grade 字段 (v1.19 已有) — 0 API 调用
function IntentSignalOverview({
  customersData,
}: {
  customersData:
    | {
        items: Array<{
          id: string;
          name: string;
          country: string;
          city?: string;
          grade?: 'A' | 'B' | 'C' | 'D' | null;
          updatedAt: number;
        }>;
      }
    | undefined;
}) {
  const all = customersData?.items ?? [];
  // 按 grade 排序 (A 在前, D 在后, null 排最后)
  const sorted = [...all]
    .sort((a, b) => {
      const ga = GRADE_ORDER[a.grade ?? 'Z'] ?? 99;
      const gb = GRADE_ORDER[b.grade ?? 'Z'] ?? 99;
      if (ga !== gb) return ga - gb;
      return b.updatedAt - a.updatedAt;
    })
    .slice(0, 5);

  // 统计各 grade 数量
  const stats = { A: 0, B: 0, C: 0, D: 0, none: 0 };
  for (const c of all) {
    if (c.grade === 'A') stats.A++;
    else if (c.grade === 'B') stats.B++;
    else if (c.grade === 'C') stats.C++;
    else if (c.grade === 'D') stats.D++;
    else stats.none++;
  }

  if (all.length === 0) {
    return null; // 没客户就不显示
  }

  return (
    <Card padding="md" className="animate-fade-up">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm sm:text-base font-semibold text-ink flex items-center gap-1.5">
          <span className="w-1 h-4 bg-accent rounded-sm" aria-hidden />
          <Icon name="zap" size="sm" className="text-accent" aria-hidden />
          Intent Signal 概览
          <span className="text-[10px] sm:text-xs text-ink-3 font-normal ml-1">
            ({all.length} 客户, 实时 A/B/C)
          </span>
        </h2>
        <Link
          to="/sourcing/intent"
          className="text-xs text-accent hover:underline flex items-center gap-1 min-h-[36px] px-2"
        >
          <Icon name="zap" size="xs" aria-hidden />1 键拓客
        </Link>
      </div>

      {/* 等级统计 — 5 个 chip (A/B/C/D/未评分) */}
      <div className="flex flex-wrap gap-1.5 mb-3 text-[10px]">
        <span className="px-2 py-0.5 rounded-full bg-success/10 text-success font-semibold">
          🟢 A {stats.A}
        </span>
        <span className="px-2 py-0.5 rounded-full bg-warn/10 text-warn font-semibold">
          🟡 B {stats.B}
        </span>
        <span className="px-2 py-0.5 rounded-full bg-ink-3/10 text-ink-3 font-semibold">
          ⚪ C {stats.C}
        </span>
        {stats.D > 0 && (
          <span className="px-2 py-0.5 rounded-full bg-danger/10 text-danger font-semibold">
            🔴 D {stats.D}
          </span>
        )}
        {stats.none > 0 && (
          <span className="px-2 py-0.5 rounded-full bg-surface-2 text-ink-3 font-semibold">
            未评分 {stats.none}
          </span>
        )}
      </div>

      {/* 客户列表 — 每行 grade badge + 公司名 + 国家 + 更新日 */}
      <div className="space-y-1.5">
        {sorted.map((c) => {
          const rec =
            c.grade === 'A'
              ? '立即联系'
              : c.grade === 'B'
                ? '1 周内联系'
                : c.grade === 'C'
                  ? '观望 / 跳过'
                  : c.grade === 'D'
                    ? '跳过'
                    : '未评分';
          return (
            <Link
              key={c.id}
              to="/customers/$customerId"
              params={{ customerId: c.id }}
              className="flex items-center gap-2.5 p-2 sm:p-2.5 bg-surface-2/40 hover:bg-surface-2 rounded transition-colors min-h-[44px] group"
              title={`${c.name} — ${rec}`}
            >
              {/* 桌面: badge 在列首; 移动: badge 在公司名右侧 */}
              <span className="hidden sm:inline-flex shrink-0">
                <IntentBadge grade={c.grade} variant="compact" recommendation={rec} />
              </span>
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <span className="text-sm font-medium text-ink truncate">{c.name}</span>
                <span className="sm:hidden shrink-0">
                  <IntentBadge grade={c.grade} variant="compact" recommendation={rec} />
                </span>
              </div>
              <span className="text-[10px] text-ink-3 shrink-0 hidden sm:inline">
                {c.country}
                {c.city ? ` · ${c.city}` : ''}
              </span>
              <Icon
                name="chevron-right"
                size="sm"
                className="text-ink-3 group-hover:text-accent shrink-0"
                aria-hidden
              />
            </Link>
          );
        })}
      </div>

      {all.length > 5 && (
        <p className="text-[10px] text-ink-3 text-center mt-2">
          还有 {all.length - 5} 个客户 ·{' '}
          <Link to="/customers" className="text-accent hover:underline">
            查看全部
          </Link>
        </p>
      )}
    </Card>
  );
}
